import { Send, Paperclip, X as CloseIcon } from "lucide-react";
import { PoweredBy } from "@/constants/images";
import { useEffect, useRef } from "react";

interface ChatInputProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: (e: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  isResumingWorkflow: boolean;
  primaryColor: string;
  placeholder?: string;
  conversationStatus: string;
  workflowState: "idle" | "active";
  showMediaUpload: boolean;
  uploadingMedia: boolean;
  onToggleMediaUpload: () => void;
  onMediaSelect: (file: File) => void;
  isAskMedia?: boolean;
}

export default function ChatInput({
  inputValue,
  onInputChange,
  onSend,
  onKeyPress,
  isLoading,
  isResumingWorkflow,
  primaryColor,
  placeholder,
  conversationStatus,
  workflowState,
  showMediaUpload,
  uploadingMedia,
  onToggleMediaUpload,
  onMediaSelect,
  isAskMedia = false,
}: ChatInputProps) {
  const getPlaceholder = () => {
    if (
      conversationStatus === "agent_active" ||
      conversationStatus === "pending_agent"
    ) {
      return "Type your message...";
    }
    if (workflowState === "active" || isResumingWorkflow) {
      return "Type your response...";
    }
    return placeholder || "Ask me anything...";
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isLoading && !showMediaUpload) {
      inputRef.current?.focus();
    }
  }, [isLoading, showMediaUpload]);

  const showAttachButton =
    conversationStatus === "agent_active" ||
    conversationStatus === "pending_agent" ||
    isAskMedia;

  return (
    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shrink-0">
      {/* Media Upload Interface */}
      {showMediaUpload && (
        <div className="mb-4 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          onClick={() => document.getElementById('file-upload-input')?.click()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleMediaUpload();
            }}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-10"
          >
            <CloseIcon size={16} />
          </button>
          <div className="flex flex-col items-center justify-center space-y-3">
            {/* <CloudUpload size={32} className="text-gray-400" /> */}
            <p className="text-base font-medium text-gray-700 dark:text-gray-300">Choose a file or drag & drop it here</p>
            <p className="text-xs text-gray-500 dark:text-gray-300">JPEG, PNG, PDF, and MP4 formats, up to 50MB</p>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onMediaSelect(file);
                }
              }}
              disabled={uploadingMedia}
              className="hidden"
              id="file-upload-input"
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
            />
          </div>
          {uploadingMedia && (
            <p className="text-xs text-gray-500 dark:text-gray-300 mt-2">
              Uploading...
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          ref={inputRef}
          onKeyPress={onKeyPress}
          placeholder={getPlaceholder()}
          disabled={isLoading || showMediaUpload}
          className="flex-1 px-4 py-2.5 border dark:border-gray-700 dark:text-gray-200 border-gray-300 rounded-sm focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all text-sm disabled:bg-gray-100 dark:bg-gray-800 outline-none"
          style={{
            ["--tw-ring-color" as any]: primaryColor,
          }}
          data-testid="chat-input-id"
        />

        {/* Media Upload Button */}
        {showAttachButton && (
          <button
            data-testid="media-clip" // ✅ test id added
            onClick={onToggleMediaUpload}
            disabled={isLoading}
            className="px-3 py-2.5 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Attach file"
          >
            <Paperclip size={18} />
          </button>
        )}

        <button
          onClick={() => onSend(inputValue)}
          disabled={isLoading || !inputValue.trim() || showMediaUpload}
          className="px-4 py-2.5 rounded-sm text-white flex items-center justify-center transition-all hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: primaryColor,
          }}
          data-testid="send-btn-id"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      {/* Powered By */}
      <div className="flex items-center justify-center gap-1 mt-3">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          Powered by
        </span>
        <div className="flex items-center gap-1">
          <img
            src={PoweredBy}
            alt="powered by thequickassist.co"
            className="w-3 h-3"
            height={100}
            width={100}
          />
          <span className="text-[10px] font-bold text-blue-500">
            TheQuickAssist.co
          </span>
        </div>
      </div>
    </div>
  );
}
